import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import ApiInspector from '../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../components/BopRowActions';
import BopExcelTable, { BopExcelColumn } from '../../components/BopExcelTable';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import { API_DOCS } from './CausaliMovimentoPage.api';

const MODULO = 'magazzino.causali_movimento';

type TipoCausaleMovimento = 'CARICO' | 'SCARICO' | 'TRASFERIMENTO' | 'RETTIFICA';

const TIPO_LABEL: Record<TipoCausaleMovimento, string> = {
  CARICO: 'Carico',
  SCARICO: 'Scarico',
  TRASFERIMENTO: 'Trasferimento',
  RETTIFICA: 'Rettifica inventariale',
};

const TIPO_BADGE_VARIANT: Record<TipoCausaleMovimento, string> = {
  CARICO: 'bop-badge--success',
  SCARICO: 'bop-badge--warning',
  TRASFERIMENTO: 'bop-badge--info',
  RETTIFICA: 'bop-badge--neutral',
};

interface Causale {
  id: number;
  societaId: number;
  codice: string;
  descrizione: string;
  tipo: TipoCausaleMovimento;
  aggiornaCmp: boolean;
  isDefault: boolean;
  attivo: boolean;
  note: string | null;
  nMovimenti?: number;
}

interface FormState {
  id: number | null;
  codice: string;
  descrizione: string;
  tipo: TipoCausaleMovimento;
  aggiornaCmp: boolean;
  isDefault: boolean;
  note: string;
}

const FORM_NUOVO: FormState = {
  id: null,
  codice: '',
  descrizione: '',
  tipo: 'CARICO',
  aggiornaCmp: true,
  isDefault: false,
  note: '',
};

export default function CausaliMovimentoPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const canDelete = can(MODULO, 'delete');

  const { societaId, setSocietaId, listaSocieta, isUnica, pronta } = useFiltroSocieta();

  const [items, setItems] = useState<Causale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mostraDisattive, setMostraDisattive] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState<'' | TipoCausaleMovimento>('');
  const [ricerca, setRicerca] = useState('');

  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!pronta) return;
    if (!societaId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      // @api GET /api/causali-movimento?societaId= -> { success: true, data: [{ id, societaId, codice, descrizione, tipo, aggiornaCmp, isDefault, attivo, note, nMovimenti }] }
      const res = await apiFetch<{ success: boolean; data: Causale[] }>(`/api/causali-movimento?societaId=${societaId}`);
      setItems(res.data ?? []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [societaId, pronta]);

  const filtered = useMemo(() => {
    const q = ricerca.trim().toLowerCase();
    return items.filter(c => {
      if (!mostraDisattive && !c.attivo) return false;
      if (filtroTipo && c.tipo !== filtroTipo) return false;
      if (q && !(c.codice.toLowerCase().includes(q) || c.descrizione.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [items, ricerca, filtroTipo, mostraDisattive]);

  const apriNuova = () => { setForm({ ...FORM_NUOVO }); setFormError(''); };
  const apriModifica = (c: Causale) => {
    setForm({
      id: c.id,
      codice: c.codice,
      descrizione: c.descrizione,
      tipo: c.tipo,
      aggiornaCmp: c.aggiornaCmp,
      isDefault: c.isDefault,
      note: c.note ?? '',
    });
    setFormError('');
  };
  const chiudi = () => { setForm(null); setFormError(''); };

  const salva = async () => {
    if (!form) return;
    const codice = form.codice.trim();
    const descrizione = form.descrizione.trim();
    if (!codice) { setFormError('Codice obbligatorio'); return; }
    if (!/^[A-Za-z0-9_-]+$/.test(codice)) { setFormError('Il codice può contenere solo lettere, numeri, "-" e "_"'); return; }
    if (!descrizione) { setFormError('Descrizione obbligatoria'); return; }

    setSaving(true);
    setFormError('');
    try {
      const payload = {
        codice,
        descrizione,
        tipo: form.tipo,
        aggiornaCmp: form.aggiornaCmp,
        isDefault: form.isDefault,
        note: form.note.trim() || null,
      };
      if (form.id === null) {
        // @api POST /api/causali-movimento { societaId, codice, descrizione, tipo, aggiornaCmp, isDefault, note } -> { success: true, data: { id } }
        await apiFetch('/api/causali-movimento', {
          method: 'POST',
          body: JSON.stringify({ societaId: Number(societaId), ...payload }),
        });
      } else {
        // @api PUT /api/causali-movimento/:id { codice, descrizione, tipo, aggiornaCmp, isDefault, note } -> { success: true }
        await apiFetch(`/api/causali-movimento/${form.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      }
      chiudi();
      load();
    } catch (e: any) {
      setFormError(e.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const elimina = async (c: Causale) => {
    if (!window.confirm(`Disattivare la causale ${c.codice} — ${c.descrizione}?`)) return;
    try {
      // @api DELETE /api/causali-movimento/:id -> { success: true }
      await apiFetch(`/api/causali-movimento/${c.id}`, { method: 'DELETE' });
      load();
    } catch (e: any) {
      window.alert(e.message || 'Errore nella disattivazione');
    }
  };

  const noSocieta = !societaId;

  const columns = useMemo<BopExcelColumn<Causale>[]>(() => [
    {
      key: 'codice',
      header: 'Codice',
      accessor: (c) => c.codice,
      filter: { type: 'text' },
      width: '140px',
      render: (c) => (
        <>
          <strong>{c.codice}</strong>
          {c.isDefault && <span title="Default per tipo">&nbsp;★</span>}
        </>
      ),
    },
    {
      key: 'descrizione',
      header: 'Descrizione',
      accessor: (c) => c.descrizione,
      filter: { type: 'text' },
    },
    {
      key: 'tipo',
      header: 'Tipo',
      accessor: (c) => c.tipo,
      filter: {
        type: 'enum',
        options: (Object.keys(TIPO_LABEL) as TipoCausaleMovimento[])
          .map(t => ({ value: t, label: TIPO_LABEL[t] })),
      },
      width: '160px',
      render: (c) => (
        <span className={`bop-badge ${TIPO_BADGE_VARIANT[c.tipo]}`}>
          {TIPO_LABEL[c.tipo]}
        </span>
      ),
    },
    {
      key: 'aggiornaCmp',
      header: 'Aggiorna CMP',
      accessor: (c) => c.aggiornaCmp ? 'SI' : 'NO',
      filter: {
        type: 'enum',
        options: [
          { value: 'SI', label: 'Sì' },
          { value: 'NO', label: 'No' },
        ],
      },
      width: '120px',
      render: (c) => c.aggiornaCmp ? 'Sì' : <span className="bop-text-muted">No</span>,
    },
    {
      key: 'nMovimenti',
      header: 'Movimenti',
      accessor: (c) => c.nMovimenti ?? 0,
      filter: { type: 'number' },
      width: '110px',
      headerClassName: 'bop-text-center',
      cellClassName: 'bop-text-center',
      render: (c) => c.nMovimenti ?? 0,
    },
    {
      key: 'attivo',
      header: 'Stato',
      accessor: (c) => c.attivo ? 'ATTIVA' : 'DISATTIVA',
      filter: {
        type: 'enum',
        options: [
          { value: 'ATTIVA',    label: 'Attiva' },
          { value: 'DISATTIVA', label: 'Disattiva' },
        ],
      },
      width: '110px',
      render: (c) => (
        <span className={`bop-badge ${c.attivo ? 'bop-badge--success' : 'bop-badge--neutral'}`}>
          {c.attivo ? 'Attiva' : 'Disattiva'}
        </span>
      ),
    },
  ], []);

  return (
    <Layout>
      <div className="bop-page-header">
        <div>
          <h2 className="bop-page-title">Causali movimento</h2>
          <p className="bop-text-muted">
            Template per i movimenti di magazzino — definiscono se il movimento è un carico, scarico, trasferimento o rettifica, e se contribuisce al ricalcolo del costo medio ponderato.
          </p>
        </div>
        {canEdit && (
          <button className="bop-btn-primary" onClick={apriNuova} disabled={noSocieta}>+ Nuova causale</button>
        )}
      </div>

      <div className="bop-filter-bar">
        {!isUnica && (
          <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
            <option value="">Seleziona società…</option>
            {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        <input
          className="bop-input"
          placeholder="Cerca per codice o descrizione…"
          value={ricerca}
          onChange={e => setRicerca(e.target.value)}
        />
        <select className="bop-select" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value as any)}>
          <option value="">Tutti i tipi</option>
          {(Object.keys(TIPO_LABEL) as TipoCausaleMovimento[]).map(t => (
            <option key={t} value={t}>{TIPO_LABEL[t]}</option>
          ))}
        </select>
        <label className="bop-flex">
          <input type="checkbox" checked={mostraDisattive} onChange={e => setMostraDisattive(e.target.checked)} />
          <span>&nbsp;Mostra anche disattive</span>
        </label>
      </div>

      {error && <div className="bop-inline-error">{error}</div>}

      {noSocieta ? (
        <div className="bop-card">
          <div className="bop-empty-state">Seleziona una società per gestire le sue causali di movimento.</div>
        </div>
      ) : (
        <BopExcelTable<Causale>
          data={filtered}
          loading={loading}
          rowKey={(c) => c.id}
          onRowClick={canEdit ? (c) => apriModifica(c) : undefined}
          initialSort={{ key: 'codice', dir: 'asc' }}
          emptyState={
            <div className="bop-empty-state">
              {items.length === 0
                ? 'Nessuna causale presente. Clicca "+ Nuova causale" per crearne una.'
                : 'Nessun risultato per i filtri impostati.'}
            </div>
          }
          columns={columns}
          rowActions={(c) => (
            <BopRowActions actions={[
              { icon: <RowIcon.Edit />, label: 'Modifica', onClick: () => apriModifica(c), hidden: !canEdit },
              { icon: <RowIcon.Disable />, label: 'Disattiva', onClick: () => elimina(c), variant: 'danger', hidden: !canDelete || !c.attivo },
            ]} />
          )}
        />
      )}

      {form && (
        <Modal
          title={form.id === null ? 'Nuova causale movimento' : `Modifica causale ${form.codice}`}
          onClose={chiudi}
          footer={
            <>
              <button className="bop-btn-secondary" onClick={chiudi}>Annulla</button>
              <button className="bop-btn-primary" onClick={salva} disabled={saving}>
                {saving ? 'Salvo…' : 'Salva'}
              </button>
            </>
          }
        >
          {formError && <div className="bop-inline-error">{formError}</div>}

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Codice *</label>
              <input
                className="bop-input"
                value={form.codice}
                onChange={e => setForm(f => f && { ...f, codice: e.target.value })}
                placeholder="es. CAR-FORN, SCAR-CLI, TRASF, RET-INV"
                autoFocus
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Tipo *</label>
              <select
                className="bop-select"
                value={form.tipo}
                onChange={e => setForm(f => f && { ...f, tipo: e.target.value as TipoCausaleMovimento })}
              >
                {(Object.keys(TIPO_LABEL) as TipoCausaleMovimento[]).map(t => (
                  <option key={t} value={t}>{TIPO_LABEL[t]}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Descrizione *</label>
            <input
              className="bop-input"
              value={form.descrizione}
              onChange={e => setForm(f => f && { ...f, descrizione: e.target.value })}
              placeholder="es. Carico da fornitore, Scarico vendita a cliente, Trasferimento a commessa…"
            />
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-flex">
                <input
                  type="checkbox"
                  checked={form.aggiornaCmp}
                  onChange={e => setForm(f => f && { ...f, aggiornaCmp: e.target.checked })}
                />
                <span>&nbsp;Aggiorna costo medio ponderato (CMP)</span>
              </label>
            </div>
            <div className="bop-form-group">
              <label className="bop-flex">
                <input
                  type="checkbox"
                  checked={form.isDefault}
                  onChange={e => setForm(f => f && { ...f, isDefault: e.target.checked })}
                />
                <span>&nbsp;Default per questo tipo</span>
              </label>
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Note</label>
            <textarea
              className="bop-input"
              rows={2}
              value={form.note}
              onChange={e => setForm(f => f && { ...f, note: e.target.value })}
            />
          </div>
        </Modal>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
